import PropTypes from 'prop-types'
import {useCallback, useEffect, useRef, useState} from 'react'

import {photoAlt} from './StoryPhotoPrints'
import {CloseIcon, DownloadIcon, PhotoIcon} from './icons'

import './storyPhotoViewer.scss'

const FADE_MS = 140
// A touch moving this far picks a direction; past the step or close distance
// it swipes to the next photo or dismisses the viewer.
const SWIPE_DIRECTION_PX = 10
const SWIPE_STEP_PX = 40
const SWIPE_CLOSE_PX = 70
// The cover is at most this long on its long side, the stored copy at most
// the second: the API only ever shrinks a photo.
const COVER_LONG_SIDE = 800
const STORED_LONG_SIDE = 2048
// The viewer's padding around the photo, and the photo's white frame.
const GUTTER_PX = 48
const FRAME_PX = 16

const reducedMotion = () => Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)

/**
 * The size a photo shows at: as large as fits the screen, but never past the
 * stored copy (a cover shorter than the cover size means the photo itself is
 * that small). Worked out from whichever copy loads first and kept, so a full
 * copy replaces the cover in place instead of growing.
 */
export const photoDisplaySize = ({width, height, viewportWidth, viewportHeight}) => {
  const longSide = Math.max(width, height)
  const largest = longSide < COVER_LONG_SIDE ? longSide : STORED_LONG_SIDE
  const scale = Math.min(
    (viewportWidth - GUTTER_PX - FRAME_PX) / width,
    (viewportHeight - GUTTER_PX - FRAME_PX) / height,
    largest / longSide
  )

  return {width: Math.round(width * scale), height: Math.round(height * scale)}
}

/**
 * What a downloaded photo is saved as: the photo's own name with the stored
 * copy's extension (a HEIC was stored as a JPG).
 */
export const downloadFilename = (photo, src) => {
  const base = (photo.filename || 'photo').replace(/\.[^.]+$/, '') || 'photo'
  const extension = (/^data:image\/png/).test(src || '') ? 'png' : 'jpg'

  return `${base}.${extension}`
}

/**
 * Story photos seen larger over a dimmed background (TK-3108). `sources` are
 * the full stored copies; `covers`, when given, show until a full copy lands,
 * and the photo keeps the size its first copy set. Escape, or a click on the
 * photo or background, closes it, and `onClose` gets the index last shown.
 *
 * With `controls` (Preview, AC 5.5–5.8, 6.4) a bar shows a counter (with more
 * than one photo), Download and Close; clicks on it never close the viewer.
 * Left/Right or a sideways swipe move through these photos only, wrapping,
 * with a short cross-fade; a swipe down closes; Tab stays between Download
 * and Close. Without them (the writing screen, AC 3.2) it's the photo alone.
 */
const StoryPhotoViewer = ({photos, sources, covers = {}, initialIndex = 0, controls = true, onClose}) => {
  const [index, setIndex] = useState(initialIndex)
  const [fading, setFading] = useState(false)
  const [sizes, setSizes] = useState({})
  const [drag, setDrag] = useState(null)
  const [announcement, setAnnouncement] = useState('')
  const dialogRef = useRef(null)
  const downloadRef = useRef(null)
  const closeRef = useRef(null)
  const indexRef = useRef(initialIndex)
  const touchRef = useRef(null)
  const suppressClickRef = useRef(false)
  const fadeTimerRef = useRef(null)

  const count = photos.length
  const photo = photos[index]
  const uploadId = photo['upload-id']
  const fullSrc = sources[uploadId]
  const src = fullSrc || covers[uploadId]
  const navigable = controls && count > 1

  indexRef.current = index

  // Closing hands focus back to whatever opened the viewer, which the focus
  // trap below would otherwise take back.
  const closingRef = useRef(false)
  const close = useCallback(() => {
    closingRef.current = true
    onClose(indexRef.current)
  }, [onClose])

  const step = useCallback(direction => {
    if (!navigable) return

    const next = (indexRef.current + direction + count) % count

    setAnnouncement(`Photo ${next + 1} of ${count}`)
    clearTimeout(fadeTimerRef.current)

    if (reducedMotion()) {
      setIndex(next)

      return
    }

    setFading(true)
    fadeTimerRef.current = setTimeout(() => {
      setIndex(next)
      setFading(false)
    }, FADE_MS)
  }, [count, navigable])

  useEffect(() => () => clearTimeout(fadeTimerRef.current), [])

  useEffect(() => {
    const scroller = dialogRef.current.closest('.life-story-story, .life-story-flow')

    if (!scroller) return

    const previousOverflow = scroller.style.overflowY

    scroller.style.overflowY = 'hidden'

    return () => { scroller.style.overflowY = previousOverflow }
  }, [])

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        // Kept from the screen beneath, which has its own Escape handling.
        event.preventDefault()
        event.stopPropagation()
        close()
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        if (!navigable) return

        event.preventDefault()
        step(event.key === 'ArrowRight' ? 1 : -1)
      } else if (event.key === 'Tab') {
        // Focus stays in the viewer, cycling between Download and Close, or
        // on the dialog itself when it has no controls.
        const items = controls ? [downloadRef.current, closeRef.current].filter(item => item && !item.hasAttribute('aria-disabled')) : []

        if (items.length === 0) {
          event.preventDefault()
          dialogRef.current.focus()

          return
        }

        const first = items[0]
        const last = items[items.length - 1]
        const current = document.activeElement

        if (event.shiftKey && (current === first || current === dialogRef.current)) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && (current === last || current === dialogRef.current)) {
          event.preventDefault()
          first.focus()
        }
      }
    }

    // The screen beneath moves focus on its own (Undo once a refine lands,
    // the wand after an interview), and Space or Enter would then act on it
    // behind the dialog; focus is taken back as soon as it leaves.
    const handleFocusIn = event => {
      if (!closingRef.current && dialogRef.current && !dialogRef.current.contains(event.target)) dialogRef.current.focus()
    }

    dialogRef.current.focus()
    document.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('focusin', handleFocusIn)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [close, controls, navigable, step])

  // Each photo is sized from the first of its copies to load, so the full
  // copy replaces the cover at the same size (see photoDisplaySize).
  const fitToScreen = useCallback((id, natural) => {
    setSizes(current => ({...current, [id]: {natural, size: photoDisplaySize({...natural, viewportWidth: window.innerWidth, viewportHeight: window.innerHeight})}}))
  }, [])

  const handleImageLoad = event => {
    if (sizes[uploadId]) return

    fitToScreen(uploadId, {width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight})
  }

  useEffect(() => {
    const refit = () => Object.entries(sizes).forEach(([id, {natural}]) => fitToScreen(id, natural))

    window.addEventListener('resize', refit)

    return () => window.removeEventListener('resize', refit)
  }, [sizes, fitToScreen])

  /* ---------- Touch (with controls): swipe sideways to move, down to close ---------- */

  const handlePointerDown = event => {
    touchRef.current = controls && event.pointerType === 'touch' ?
      {startX: event.clientX, startY: event.clientY, pointerId: event.pointerId, mode: null} :
      null
  }

  const handlePointerMove = event => {
    const touch = touchRef.current

    if (!touch || event.pointerId !== touch.pointerId) return

    const dx = event.clientX - touch.startX
    const dy = event.clientY - touch.startY

    if (!touch.mode && Math.max(Math.abs(dx), Math.abs(dy)) > SWIPE_DIRECTION_PX) touch.mode = Math.abs(dx) > Math.abs(dy) ? 'sideways' : 'down'
    if (touch.mode === 'down' && dy > 0) setDrag({dy})
  }

  const handlePointerUp = event => {
    const touch = touchRef.current

    if (!touch || event.pointerId !== touch.pointerId) return

    touchRef.current = null

    if (!touch.mode) return

    // The swipe isn't also a tap that closes the viewer.
    suppressClickRef.current = true
    setTimeout(() => { suppressClickRef.current = false }, 0)

    const dx = event.clientX - touch.startX
    const dy = event.clientY - touch.startY

    if (touch.mode === 'sideways' && Math.abs(dx) >= SWIPE_STEP_PX) {
      setDrag(null)
      step(dx < 0 ? 1 : -1)
    } else if (touch.mode === 'down' && dy >= SWIPE_CLOSE_PX) {
      if (reducedMotion()) {
        close()

        return
      }

      setDrag({dy: dy + 120, leaving: true})
      setTimeout(() => {
        if (!closingRef.current && dialogRef.current) close()
      }, FADE_MS)
    } else {
      setDrag(null)
    }
  }

  const handleClick = event => {
    if (suppressClickRef.current || event.target.closest('.story-photo-viewer-bar')) return

    close()
  }

  const sized = sizes[uploadId]
  const imageStyle = {
    ...(sized ? sized.size : {visibility: 'hidden'}),
    ...(drag ? {translate: `0 ${drag.dy}px`, opacity: drag.leaving ? 0 : Math.max(0.25, 1 - (drag.dy / 420))} : {})
  }

  const image = () => {
    if (src) {
      return (
        <img
          alt={photoAlt(photo)}
          className={`story-photo-viewer-image${fading ? ' fading' : ''}${drag && drag.leaving ? ' leaving' : ''}`}
          key={uploadId}
          src={src}
          style={imageStyle}
          onLoad={handleImageLoad}
        />
      )
    }

    if (fullSrc === null) return <span className='story-photo-viewer-unavailable'><PhotoIcon size={40} /><span className='sr-only'>{photoAlt(photo)}</span></span>

    return <span className='story-photo-viewer-spinner' />
  }

  return (
    <div
      aria-label='Photo'
      aria-modal='true'
      className='story-photo-viewer'
      ref={dialogRef}
      role='dialog'
      tabIndex={-1}
      onClick={handleClick}
      onPointerCancel={() => { touchRef.current = null; setDrag(null) }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}>
      {
        controls &&
          <div className='story-photo-viewer-bar'>
            {count > 1 && <span className='story-photo-viewer-count'>{index + 1} of {count}</span>}
            <div className='story-photo-viewer-actions'>
              <a
                aria-disabled={fullSrc ? null : true}
                aria-label='Download this photo'
                className='story-photo-viewer-button'
                download={fullSrc ? downloadFilename(photo, fullSrc) : null}
                href={fullSrc || null}
                ref={downloadRef}>
                <DownloadIcon />
              </a>
              <button aria-label='Close' className='story-photo-viewer-button' ref={closeRef} type='button' onClick={close}>
                <CloseIcon size={16} />
              </button>
            </div>
          </div>
      }
      {image()}
      {controls && <div aria-live='polite' className='sr-only'>{announcement}</div>}
    </div>
  )
}

StoryPhotoViewer.propTypes = {
  controls: PropTypes.bool,
  covers: PropTypes.object,
  initialIndex: PropTypes.number,
  photos: PropTypes.arrayOf(PropTypes.shape({'upload-id': PropTypes.string, filename: PropTypes.string})).isRequired,
  sources: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
}

export default StoryPhotoViewer
